// =============================================================================
// eodInsights.js — the Previous-Week End of Day tile, read from Supabase.
//
// GATED OFF. EOD_INSIGHTS_READ_HOSTS is empty, so every tablet still asks the
// Google backend exactly as it does today. Switching a shop on is a reviewed
// one-line host add; taking it back off is deleting that line.
//
// WHY THIS FILE EXISTS AT ALL. The tile has been called "built, awaiting a flip"
// for weeks. It wasn't: 0131's feed is service_role only, so a tablet could never
// have called it — there was nothing to point at. Migration 0343 adds the narrow
// one-shop-one-day reader a till can actually ask for, and this is the client
// half of it.
//
// The sheet stays the safety net. Any failure at all — no session, no row, a
// network blip, a bad shape — falls straight back to the same backend call the
// tile makes today, so the worst case is today's behaviour.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { ensureAnonSession } from "./stockSync.js";

// --- the gate ---------------------------------------------------------------
const EOD_INSIGHTS_READ_HOSTS = new Set([
  "mcr-kitchen.eatrolled.com",   // ← Kitchen app's Previous-Week Insights → Supabase (Lane AM, 21 Aug 2026); prod parity CLEAN. The FOH tile is hidden, so this host is where the tile is actually shown.
  "trafford.eatrolled.com",      // ← 19 Sep 2026 (kitchen → Trafford merge, Day-1): the kitchen team now reads Insights on the Trafford app. Same reader + parity as the kitchen host above; the tile stays hidden on the till until the CC flips tc/insights live. Revert = delete this line.
]);
export function eodInsightsReadEnabled() {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get("eodinsightsread");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return EOD_INSIGHTS_READ_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// The shape the tile already renders. Kept here so a malformed answer can never
// reach the screen half-built — anything that isn't this falls back to the sheet.
function looksLikeADay(d, siteKey, date) {
  return !!d
    && typeof d === "object"
    && !Array.isArray(d)
    && String(d.date || "") === String(date)
    && Array.isArray(d.soldOut)
    && Array.isArray(d.wastage);
}

// One shop, one day. `sheetUrl` is the existing backend call, used as the fallback.
export async function fetchEodInsightsDay(siteKey, date, { sheetUrl } = {}) {
  const fallback = async (why, isError) => {
    // isError=true is a real failure worth grepping for. Everything else is the
    // ordinary "nothing there, ask the sheet" path and stays quiet.
    if (isError) console.warn("[eod-insights] fell back to the sheet:", why);
    if (!sheetUrl) return null;
    try { const r = await fetch(sheetUrl); return r.ok ? await r.json() : null; }
    catch (e) { return null; }
  };

  try { await ensureAnonSession(); }
  catch (e) { return fallback("no session: " + (e?.message || e), true); }

  let data;
  try {
    const res = await supabase.rpc("forecast_eod_insights_day", { p_site: siteKey, p_date: date });
    if (res.error) throw res.error;
    data = res.data;
  } catch (e) {
    return fallback("supabase error: " + (e?.message || e), true);
  }

  // A plain null is 0343 saying "I cannot answer for that day" — it is older than
  // the 31 days the underlying feed can reach. That is NOT the same as "nothing
  // happened", and the sheet still has the day, so go and get it. Logged
  // separately from a genuinely malformed answer so the two are tellable apart.
  if (data === null || data === undefined) return fallback("outside the 31-day window this view can see — the full history has it", true);
  if (!looksLikeADay(data, siteKey, date)) return fallback("unexpected shape", true);

  // A day with no report comes back in the tile's own empty shape (0343 builds
  // it), so "nobody submitted" renders as an empty day rather than an error.
  return data;
}
